import { AccountController } from "@/app/ui/controllers/AccountController";

export type FormatByCountryType =
  | "money"
  | "weight"
  | "dimension"
  | "quantity"
  | "percentage";

export type FormatCountry = "ID" | "MY";

export type FormatByCountryOptions = {
  type: FormatByCountryType;
  country?: string;
  suffix?: string;
  prefix?: string;
  fractionDigits?: number;
  minFractionDigits?: number;
};

export const LOCALE_BY_COUNTRY: Record<FormatCountry, string> = {
  ID: "id-ID",
  MY: "ms-MY",
};

export function resolveCountry(raw?: string): FormatCountry {
  const source =
    raw ?? AccountController.accountData?.countryCode ?? "ID";
  const normalized = String(source).trim().toUpperCase();
  if (normalized === "MY" || normalized === "MYS") return "MY";
  return "ID";
}

export function separatorsByCountry(country: FormatCountry): {
  thousand: string;
  decimal: string;
} {
  return country === "MY"
    ? { thousand: ",", decimal: "." }
    : { thousand: ".", decimal: "," };
}

function defaultFractionDigits(
  type: FormatByCountryType,
  value: number,
  override?: number,
  minFractionDigits?: number
): { minimumFractionDigits: number; maximumFractionDigits: number } {
  if (override !== undefined) {
    return {
      minimumFractionDigits: minFractionDigits ?? override,
      maximumFractionDigits: override
    };
  }
  if (type === "money") {
    return { minimumFractionDigits: 2, maximumFractionDigits: 2 };
  }
  if (type === "quantity") {
    return { minimumFractionDigits: 0, maximumFractionDigits: 0 };
  }
  if (type === "weight") {
    const hasFraction = !Number.isInteger(value);
    return {
      minimumFractionDigits: 0,
      maximumFractionDigits: hasFraction ? 2 : 0
    };
  }
  const hasFraction = !Number.isInteger(value);
  return {
    minimumFractionDigits: 0,
    maximumFractionDigits: hasFraction ? 20 : 0,
  };
}

function defaultSuffix(type: FormatByCountryType, suffix?: string): string {
  if (suffix !== undefined) return suffix;
  return type === "percentage" ? "%" : "";
}

export default function formatByCountry(
  value: number,
  options: FormatByCountryOptions
): string {
  const country = resolveCountry(options.country);
  const locale = LOCALE_BY_COUNTRY[country];
  const digits = defaultFractionDigits(
    options.type,
    value,
    options.fractionDigits,
    options.minFractionDigits
  );
  const prefix = options.prefix ?? "";
  const suffix = defaultSuffix(options.type, options.suffix);
  const formatted = new Intl.NumberFormat(locale, {
    useGrouping: true,
    ...digits,
  }).format(Number.isFinite(value) ? value : 0);
  return `${prefix}${formatted}${suffix}`;
}
